import { Document, Page as PdfPage, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { registerPdfFonts } from './pdf-fonts';

registerPdfFonts();

// ─── CAP proposal PDF (Phase E — CAP, PR7) ──────────────────────────────────
// Mirrors TranscriptPdfDocument: single StyleSheet, watermark, footer +
// paging, EB Garamond. i18n-AGNOSTIC — receives labels already resolved to
// ENGLISH (TMAI requires English submission, §III.A), regardless of the UI
// language. Rendered client-side (pdf().toBlob()); a submitted proposal's
// content is frozen upstream so the PDF is a deterministic re-render.

export interface CapProposalPdfSection {
  key: string;
  heading: string; // English section header (A. ... K.)
  source: 'auto' | 'manual';
  complete: boolean;
  hardGap: boolean;
  /** Rendered body: auto data summary or the TC's manual narrative. */
  body?: string;
  /** Section D advisory (English): the instructional-language rule applies. */
  note?: string;
  /** Section D caution (English): rule applies but content is still missing. */
  warning?: string;
}

export interface CapProposalPdfData {
  brand: {
    tenantName: string;
    logoUrl?: string;
    primaryColor: string;
    tmaiSiteAbbreviation?: string;
  };
  programName: string;
  award: string; // English award title
  capCode: string;
  level: string;
  generatedOn: string;
  status: 'draft' | 'ready' | 'submitted';
  sections: CapProposalPdfSection[];
  /** Printed near the top when submitting with known gaps (attested). */
  knownGapsDisclosure?: string;
  /** Itemized per-area shortfalls from the audit (e.g. "Hermeneutics — 3 TCU"). */
  complianceGaps?: string[];
  labels: {
    complianceGapsTitle: string;
    title: string;
    watermarkDraft: string;
    program: string;
    award: string;
    cap: string;
    level: string;
    generatedOn: string;
    sourceAuto: string;
    sourceManual: string;
    hardGap: string;
    incomplete: string;
    footerDisclaimer: string;
  };
}

const styles = StyleSheet.create({
  page: {
    fontFamily: 'EB Garamond',
    fontSize: 10,
    color: '#1f2937',
    paddingTop: 42,
    paddingBottom: 56,
    paddingHorizontal: 48,
  },
  watermark: {
    position: 'absolute',
    top: '42%',
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 54,
    fontWeight: 700,
    color: '#000000',
    opacity: 0.06,
    transform: 'rotate(-45deg)',
  },
  header: { textAlign: 'center', marginBottom: 16 },
  logo: { height: 48, objectFit: 'contain', marginBottom: 6, alignSelf: 'center' },
  tenantName: { fontSize: 16, fontWeight: 700, lineHeight: 1.2 },
  brandBar: { width: 64, height: 3, borderRadius: 2, alignSelf: 'center', marginVertical: 7 },
  docTitle: { fontSize: 11, letterSpacing: 2, textTransform: 'uppercase' },
  metaRow: { flexDirection: 'row', gap: 24, justifyContent: 'center', marginTop: 4 },
  metaLabel: { fontSize: 7, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 },
  metaValue: { fontSize: 10, fontWeight: 700 },
  disclosure: {
    borderWidth: 1,
    borderColor: '#d97706',
    borderRadius: 4,
    padding: 8,
    marginBottom: 14,
    backgroundColor: '#fffbeb',
    fontSize: 9,
    color: '#92400e',
  },
  section: { marginBottom: 12 },
  sectionHead: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 3 },
  heading: { fontSize: 11, fontWeight: 700 },
  badge: {
    fontSize: 7,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    paddingVertical: 1,
    paddingHorizontal: 4,
    borderRadius: 3,
  },
  badgeAuto: { backgroundColor: '#dcfce7', color: '#166534' },
  badgeManual: { backgroundColor: '#e0e7ff', color: '#3730a3' },
  badgeGap: { backgroundColor: '#fee2e2', color: '#991b1b' },
  body: { fontSize: 9.5, lineHeight: 1.4, color: '#374151' },
  gapNote: { fontSize: 9, color: '#991b1b', fontStyle: 'italic' },
  note: { fontSize: 8.5, color: '#475569', marginTop: 3 },
  warning: { fontSize: 8.5, color: '#b45309', marginTop: 3 },
  gapsBox: {
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 4,
    padding: 8,
    marginBottom: 14,
    backgroundColor: '#f8fafc',
  },
  gapsTitle: { fontSize: 9, fontWeight: 700, color: '#334155', marginBottom: 3 },
  gapsItem: { fontSize: 9, color: '#475569', lineHeight: 1.4 },
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 48,
    right: 48,
    fontSize: 8,
    textAlign: 'center',
    color: '#94a3b8',
    borderTopWidth: 0.5,
    borderTopColor: '#e2e8f0',
    paddingTop: 6,
  },
  pageNumber: { position: 'absolute', bottom: 10, right: 48, fontSize: 8, color: '#94a3b8' },
});

export function CapProposalPdfDocument({ data }: { data: CapProposalPdfData }): JSX.Element {
  const { brand, labels } = data;
  const isDraft = data.status !== 'submitted';
  return (
    <Document>
      <PdfPage size="A4" style={styles.page}>
        {isDraft && (
          <Text style={styles.watermark} fixed>
            {labels.watermarkDraft}
          </Text>
        )}

        <View style={styles.header}>
          {brand.logoUrl ? <Image style={styles.logo} src={brand.logoUrl} /> : null}
          <Text style={styles.tenantName}>
            {brand.tenantName}
            {brand.tmaiSiteAbbreviation ? ` (${brand.tmaiSiteAbbreviation})` : ''}
          </Text>
          <View style={[styles.brandBar, { backgroundColor: brand.primaryColor }]} />
          <Text style={styles.docTitle}>{labels.title}</Text>
          <View style={styles.metaRow}>
            <View>
              <Text style={styles.metaLabel}>{labels.program}</Text>
              <Text style={styles.metaValue}>{data.programName}</Text>
            </View>
            <View>
              <Text style={styles.metaLabel}>{labels.award}</Text>
              <Text style={styles.metaValue}>{data.award}</Text>
            </View>
            <View>
              <Text style={styles.metaLabel}>{labels.cap}</Text>
              <Text style={styles.metaValue}>
                {data.capCode} · {data.level}
              </Text>
            </View>
          </View>
          <Text style={[styles.metaLabel, { marginTop: 4 }]}>
            {labels.generatedOn}: {data.generatedOn}
          </Text>
        </View>

        {data.knownGapsDisclosure ? (
          <Text style={styles.disclosure}>{data.knownGapsDisclosure}</Text>
        ) : null}

        {data.complianceGaps && data.complianceGaps.length > 0 ? (
          <View style={styles.gapsBox}>
            <Text style={styles.gapsTitle}>{data.labels.complianceGapsTitle}</Text>
            {data.complianceGaps.map((g, i) => (
              <Text key={i} style={styles.gapsItem}>
                • {g}
              </Text>
            ))}
          </View>
        ) : null}

        {data.sections.map((s) => (
          <View key={s.key} style={styles.section} wrap={false}>
            <View style={styles.sectionHead}>
              <Text style={styles.heading}>{s.heading}</Text>
              <Text
                style={[styles.badge, s.source === 'auto' ? styles.badgeAuto : styles.badgeManual]}
              >
                {s.source === 'auto' ? labels.sourceAuto : labels.sourceManual}
              </Text>
              {s.hardGap ? (
                <Text style={[styles.badge, styles.badgeGap]}>{labels.hardGap}</Text>
              ) : null}
            </View>
            {s.body ? (
              <Text style={styles.body}>{s.body}</Text>
            ) : (
              <Text style={styles.gapNote}>{labels.incomplete}</Text>
            )}
            {s.note ? <Text style={styles.note}>{s.note}</Text> : null}
            {s.warning ? <Text style={styles.warning}>{s.warning}</Text> : null}
          </View>
        ))}

        <Text style={styles.footer} fixed>
          {labels.footerDisclaimer}
        </Text>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          fixed
        />
      </PdfPage>
    </Document>
  );
}
